/**
 * Contabilidade — Demonstrações Contábeis (dados abertos da ANS).
 *
 * O balancete que cada operadora entrega à ANS por trimestre (DIOPS): Ativo,
 * Passivo e DRE em árvore, a própria operadora sempre na 1ª coluna e até 3
 * outras para comparar. Resultado é ACUMULADO NO ANO até o fim do trimestre.
 * Em "%", cada linha vira proporção (DRE ÷ receitas de operações de saúde;
 * balanço ÷ ativo total) — é o que torna justa a comparação entre portes.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, RefreshCw, AlertTriangle, ChevronRight, ChevronDown } from "lucide-react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { T, MONO, InfoTip, Card, Botao, TH, TD, fmtInt, fmtPct } from "./ui";

const SECOES = [
  { chave: "dre", rotulo: "DRE", base: "dre", ajudaPct: "% das Receitas com operações de assistência à saúde (conta 31)." },
  { chave: "ativo", rotulo: "Ativo", base: "balanco", ajudaPct: "% do Ativo total (conta 1)." },
  { chave: "passivo", rotulo: "Passivo", base: "balanco", ajudaPct: "% do Passivo total, que é igual ao Ativo total (conta 1)." },
];

// Variação sobre o mesmo trimestre do ano anterior: em R$ vira %; em indicador de % vira pontos percentuais.
const variacao = (atual, ant) => (atual === null || atual === undefined || !ant ? null : (atual - ant) / Math.abs(ant));

// Seletor de operadoras para comparar (reusado em Indicadores ANS, com a busca do próprio módulo).
export function FiltroOperadoras({ proprio, nomeProprio, sel, setSel, max, urlBusca = "/contabilidade/ans/operadoras" }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [achadas, setAchadas] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const fora = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  // Busca no servidor (nome ou registro ANS), com uma pausa curta para não disparar a cada tecla.
  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2) { setAchadas([]); return undefined; }
    setBuscando(true);
    const t = setTimeout(() => {
      api.get(urlBusca, { params: { q } })
        .then((r) => setAchadas((r.data || []).filter((o) => o.registro_ans !== proprio)))
        .catch(() => toast.error("Falha ao buscar operadoras"))
        .finally(() => setBuscando(false));
    }, 300);
    return () => clearTimeout(t);
  }, [busca, proprio, urlBusca]);

  const selecionada = (reg) => sel.some((o) => o.registro_ans === reg);
  const alternar = (o) => {
    if (selecionada(o.registro_ans)) setSel(sel.filter((x) => x.registro_ans !== o.registro_ans));
    else if (sel.length < max) setSel([...sel, { registro_ans: o.registro_ans, nome: o.nome }]);
  };
  const btnMini = { fontSize: 12, padding: "3px 8px", borderRadius: 6, border: `1px solid ${T.line}`, background: "#fff", cursor: "pointer", color: T.ink };
  const chip = (cor) => ({ fontSize: 12, background: cor, color: T.ink, borderRadius: 12, padding: "2px 8px", display: "inline-flex", gap: 4, alignItems: "center" });

  return (
    <div ref={boxRef} style={{ position: "relative", flex: 1, minWidth: 320 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 8px", minHeight: 40 }}>
        <span style={{ fontSize: 12, color: T.inkSoft, fontWeight: 600, marginRight: 2 }}>Operadoras:</span>
        {proprio && (
          <span style={{ ...chip(T.tealSoft), fontWeight: 600 }} title="Sua operadora (registro ANS da tela Operadora), sempre na primeira coluna">
            {nomeProprio || proprio} <span style={{ fontSize: 10, color: T.teal }}>· sua</span>
          </span>
        )}
        {sel.map((o) => (
          <span key={o.registro_ans} style={{ ...chip("#eef2f7"), paddingRight: 4 }}>
            {o.nome} <span style={{ fontSize: 10, color: T.inkSoft }}>{o.registro_ans}</span>
            <span onClick={() => alternar(o)} title="remover" style={{ cursor: "pointer", color: T.inkSoft, fontWeight: 700, padding: "0 3px" }}>×</span>
          </span>
        ))}
        <button onClick={() => setAberto((v) => !v)} style={{ ...btnMini, marginLeft: "auto" }}>
          {aberto ? "Fechar" : `Comparar com… ▾`}
        </button>
      </div>

      {aberto && (
        <div style={{ position: "absolute", zIndex: 40, top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,.12)", padding: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome ou registro ANS…"
              style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.line}`, fontSize: 13 }} />
            <button onClick={() => setSel([])} style={btnMini} disabled={!sel.length}>Limpar</button>
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {busca.trim().length < 2 ? <p style={{ fontSize: 12, color: T.inkSoft, padding: 6 }}>Digite ao menos 2 letras do nome, ou o registro ANS.</p>
              : buscando ? <p style={{ fontSize: 12, color: T.inkSoft, padding: 6 }}>Buscando…</p>
              : achadas.length === 0 ? <p style={{ fontSize: 12, color: T.inkSoft, padding: 6 }}>Nenhuma operadora com demonstração publicada encontrada.</p>
              : achadas.map((o) => {
                const marcada = selecionada(o.registro_ans);
                const cheio = !marcada && sel.length >= max;
                return (
                  <label key={o.registro_ans} title={cheio ? `Compare no máximo ${max} operadoras por vez` : o.razao_social}
                    style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 4px", cursor: cheio ? "not-allowed" : "pointer", fontSize: 13, opacity: cheio ? 0.5 : 1 }}>
                    <input type="checkbox" checked={marcada} disabled={cheio} onChange={() => alternar(o)} />
                    <span style={{ color: T.ink }}>{o.nome}</span>
                    <span style={{ fontSize: 11, color: T.inkSoft }}>{o.registro_ans} · {o.modalidade || "—"} · {o.cidade ? `${o.cidade}/` : ""}{o.uf || ""}</span>
                    {o.ativa === false && <span style={{ fontSize: 10, color: T.risco, border: `1px solid ${T.riscoSoft}`, borderRadius: 4, padding: "0 4px" }}>cancelada</span>}
                  </label>
                );
              })}
          </div>
          <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 6, borderTop: `1px solid ${T.line}`, paddingTop: 6 }}>
            {sel.length} de {max} escolhidas (além da sua) · a escolha fica salva neste navegador.
          </div>
        </div>
      )}
    </div>
  );
}

function Indicadores({ dados, regs }) {
  const defs = dados.indicadores_def || [];
  const val = (d, v) => (v === null || v === undefined ? "—" : d.tipo === "pct" ? fmtPct(v) : d.tipo === "num" ? v.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : `R$ ${fmtInt(v)}`);
  const delta = (d, reg) => {
    const a = dados.indicadores[reg]?.[d.chave];
    const b = dados.indicadores_anterior[reg]?.[d.chave];
    if (a === null || a === undefined || b === null || b === undefined) return null;
    if (d.tipo === "pct") {
      const pp = (a - b) * 100;
      const bom = d.menor_melhor ? pp <= 0 : pp >= 0;
      return { txt: `${pp >= 0 ? "+" : ""}${pp.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.`, bom };
    }
    const v = variacao(a, b);
    if (v === null) return null;
    return { txt: `${v >= 0 ? "+" : ""}${fmtPct(v)}`, bom: d.menor_melhor ? v <= 0 : v >= 0 };
  };
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Indicadores: {dados.rotulo}</h3>
        <InfoTip w={320} texto={`Mesma base do Painel Econômico-Financeiro da ANS. Resultado acumulado no ano até o fim do trimestre. Abaixo de cada valor, a variação sobre ${dados.rotulo_anterior} (em pontos percentuais para os indicadores em %).`} />
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...TH, textAlign: "left", minWidth: 220 }}>Indicador</th>
            {dados.operadoras.map((o, i) => (
              <th key={o.registro_ans} style={{ ...TH, textAlign: "right", minWidth: 150, color: i === 0 ? T.teal : T.inkSoft }} title={`${o.registro_ans} · ${o.modalidade || ""} · ${o.uf || ""}`}>
                {o.nome}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {defs.map((d) => (
              <tr key={d.chave}>
                <td style={{ ...TD }}>{d.rotulo}<InfoTip w={300} texto={d.ajuda} /></td>
                {regs.map((reg, i) => {
                  const dl = delta(d, reg);
                  return (
                    <td key={reg} style={{ ...TD, textAlign: "right", fontFamily: MONO, background: i === 0 ? "#f7fbfa" : undefined }}>
                      <div style={{ fontWeight: 600 }}>{dados.tem_dado[reg] ? val(d, dados.indicadores[reg]?.[d.chave]) : "—"}</div>
                      {dl && <div style={{ fontSize: 11, color: dl.bom ? T.verde : T.risco }}>{dl.txt}</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Card(s) "Resultado Líquido" em destaque — como no painel da ANS, um por coluna.
function ResultadoLiquido({ dados, regs }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${regs.length}, minmax(180px, 1fr))`, gap: 12, marginBottom: 12 }}>
      {dados.operadoras.map((o, i) => {
        const v = dados.indicadores[o.registro_ans]?.resultado_liquido;
        const a = dados.indicadores_anterior[o.registro_ans]?.resultado_liquido;
        const dv = variacao(v, a);
        return (
          <Card key={o.registro_ans} style={{ padding: 14, textAlign: "center", borderColor: i === 0 ? T.teal : T.line }}>
            <div style={{ fontSize: 13, color: T.teal, fontWeight: 600 }}>Resultado Líquido</div>
            <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={o.nome}>
              {o.nome}{o.segmento ? ` (${o.qt_operadoras} operadoras)` : ""}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: v === null || v === undefined ? T.inkSoft : v >= 0 ? T.ink : T.risco, fontFamily: MONO }}>
              {v === null || v === undefined ? "—" : `R$ ${fmtInt(v)}`}
            </div>
            {dv !== null && <div style={{ fontSize: 11, color: T.inkSoft }}>{dv >= 0 ? "+" : ""}{fmtPct(dv)} vs {dados.rotulo_anterior}</div>}
          </Card>
        );
      })}
    </div>
  );
}

function Demonstracao({ dados, regs, secao, modo, comVariacao, compacto }) {
  const linhas = dados[secao.chave] || [];
  // Árvore: começa com os grupos de 2 dígitos abertos (igual ao painel da ANS).
  const [abertas, setAbertas] = useState(() => new Set(linhas.filter((l) => l.nivel <= 1).map((l) => l.conta)));
  useEffect(() => { setAbertas(new Set(linhas.filter((l) => l.nivel <= 1).map((l) => l.conta))); }, [secao.chave]);
  const temFilhos = useMemo(() => { const s = new Set(); linhas.forEach((l) => l.pai && s.add(l.pai)); return s; }, [linhas]);
  const visivel = (l) => { let p = l.pai; while (p) { if (!abertas.has(p)) return false; p = p.length > 1 ? p.slice(0, -1) : null; } return true; };
  const alternar = (c) => setAbertas((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const base = (reg) => dados.base_pct[reg]?.[secao.base];
  const celula = (l, reg) => {
    const v = l.valores[reg];
    if (v === null || v === undefined) return "—";
    if (modo === "pct") { const b = base(reg); return b ? fmtPct(v / b) : "—"; }
    return fmtInt(v);
  };
  const btnMini = { fontSize: 12, padding: "3px 8px", borderRadius: 6, border: `1px solid ${T.line}`, background: "#fff", cursor: "pointer", color: T.ink };

  if (!linhas.length) return <p style={{ color: T.inkSoft, fontSize: 13 }}>Sem contas nesta seção para o trimestre.</p>;
  return (
    <>
      {!compacto && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <button onClick={() => setAbertas(new Set(linhas.map((l) => l.conta)))} style={btnMini}>Expandir tudo</button>
          <button onClick={() => setAbertas(new Set())} style={btnMini}>Recolher tudo</button>
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...TH, textAlign: "left", minWidth: compacto ? 0 : 320 }}>
              Conta ({secao.rotulo})
              <InfoTip w={320} texto={modo === "pct" ? secao.ajudaPct : "Valor final em R$. Contas de resultado (DRE): acumulado no ano até o fim do trimestre. Balanço: saldo no fim do trimestre."} />
            </th>
            {dados.operadoras.map((o, i) => (
              <th key={o.registro_ans} colSpan={comVariacao ? 2 : 1} style={{ ...TH, textAlign: "right", color: i === 0 ? T.teal : T.inkSoft }}>{compacto ? "Valor final" : o.nome}</th>
            ))}
          </tr>
          {comVariacao && (
            <tr>
              <th style={{ ...TH, top: 33 }} />
              {regs.map((reg) => [
                <th key={`${reg}v`} style={{ ...TH, top: 33, textAlign: "right", fontWeight: 400 }}>{modo === "pct" ? "%" : "R$"}</th>,
                <th key={`${reg}d`} style={{ ...TH, top: 33, textAlign: "right", fontWeight: 400 }} title={`Variação sobre ${dados.rotulo_anterior}`}>vs {dados.rotulo_anterior}</th>,
              ])}
            </tr>
          )}
          </thead>
          <tbody>
            {linhas.filter(visivel).map((l) => {
              const grupo = temFilhos.has(l.conta);
              const negrito = l.nivel <= 2;
              return (
                <tr key={l.conta} style={{ background: l.nivel === 1 ? "#f7f9fb" : undefined }}>
                  <td style={{ ...TD, paddingLeft: 8 + (l.nivel - 1) * 16, fontWeight: negrito ? 600 : 400, cursor: grupo ? "pointer" : "default" }}
                    onClick={() => grupo && alternar(l.conta)}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {grupo ? (abertas.has(l.conta) ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : <span style={{ width: 13 }} />}
                      <span style={{ color: T.inkSoft, fontFamily: MONO, fontSize: 11 }}>{l.conta}</span>
                      <span style={{ fontStyle: l.sintetica ? "italic" : "normal" }}>{l.descricao}</span>
                      {l.sintetica && <InfoTip w={340} texto="No meio do ano, o resultado do exercício ainda está nas contas de DRE e não foi levado ao patrimônio líquido, então o passivo declarado fica menor que o ativo exatamente nesse valor. O painel da ANS soma esse resultado ao Passivo total; fazemos o mesmo e mostramos aqui quanto foi somado." />}
                    </span>
                  </td>
                  {regs.map((reg, i) => {
                    const v = variacao(l.valores[reg], l.anterior[reg]);
                    const bg = i === 0 ? "#f7fbfa" : undefined;
                    return [
                      <td key={`${reg}v`} style={{ ...TD, textAlign: "right", fontFamily: MONO, fontWeight: negrito ? 600 : 400, background: bg }}>{celula(l, reg)}</td>,
                      comVariacao && (
                        <td key={`${reg}d`} style={{ ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12, color: v === null ? T.inkSoft : v >= 0 ? T.ink : T.inkSoft, background: bg }}>
                          {v === null ? "—" : `${v >= 0 ? "+" : ""}${fmtPct(v)}`}
                        </td>
                      ),
                    ];
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function DemonstracoesContabeis() {
  const { user, temNivel } = useAuth();
  const gestor = temNivel("resultado_plano", "gestor");   // mesmo papel que o backend exige
  const chaveSel = `cont_ans_comparar_${user?.id ?? user?.email ?? "anon"}`;
  const [status, setStatus] = useState(null);
  const [tri, setTri] = useState("");
  const [sel, setSelInterno] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(chaveSel) || "[]");
      return Array.isArray(s) ? s.filter((o) => o && o.registro_ans).slice(0, 3) : [];
    } catch { return []; }
  });
  const [dados, setDados] = useState(null);
  const [secao, setSecao] = useState("dre");
  const [modo, setModo] = useState("rs");
  const [comVariacao, setComVariacao] = useState(true);
  const [nivel, setNivel] = useState(3);
  const [carregando, setCarregando] = useState(false);
  // Segmento (soma das operadoras filtradas, como o painel da ANS). Fica salvo no navegador.
  const chaveSeg = `cont_ans_segmento_${user?.id ?? user?.email ?? "anon"}`;
  const [seg, setSegInterno] = useState(() => {
    try { return { ativo: false, grupo: "", modalidade: "", uf: "", ...JSON.parse(localStorage.getItem(chaveSeg) || "{}") }; }
    catch { return { ativo: false, grupo: "", modalidade: "", uf: "" }; }
  });
  const setSeg = (mud) => setSegInterno((s) => {
    const n = { ...s, ...mud };
    try { localStorage.setItem(chaveSeg, JSON.stringify(n)); } catch { /* sem storage: só não lembra */ }
    return n;
  });
  const [opcoesSeg, setOpcoesSeg] = useState(null);
  const [ladoALado, setLadoALado] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);

  const setSel = (lista) => {
    setSelInterno(lista);
    try { localStorage.setItem(chaveSel, JSON.stringify(lista)); } catch { /* navegador sem storage: só não lembra */ }
  };

  const carregarStatus = () => api.get("/contabilidade/ans/status")
    .then((r) => { setStatus(r.data); setTri((t) => t || r.data.trimestres?.[0]?.chave || ""); })
    .catch(() => toast.error("Falha ao carregar as demonstrações"));
  useEffect(() => { carregarStatus(); }, []);

  useEffect(() => {
    if (!tri) return;
    const [ano, trimestre] = tri.split("-");
    api.get("/contabilidade/ans/segmentos", { params: { ano, trimestre } }).then((r) => setOpcoesSeg(r.data)).catch(() => setOpcoesSeg(null));
  }, [tri]);
  useEffect(() => {
    if (!opcoesSeg) return;
    const existe = (lista, v) => !v || (lista || []).some((o) => o.valor === v);
    const limpar = {};
    if (!existe(opcoesSeg.grupos, seg.grupo)) limpar.grupo = "";
    if (!existe(opcoesSeg.modalidades, seg.modalidade)) limpar.modalidade = "";
    if (!existe(opcoesSeg.ufs, seg.uf)) limpar.uf = "";
    if (Object.keys(limpar).length) setSeg(limpar);
  }, [opcoesSeg]);

  const regsSel = sel.map((o) => o.registro_ans).join(",");
  useEffect(() => {
    if (!tri) return undefined;
    let vivo = true;   // troca rápida de filtro: a resposta antiga não pode chegar por último e ficar
    const [ano, trimestre] = tri.split("-");
    setCarregando(true);
    const pSeg = seg.ativo ? { segmento: true, seg_grupo: seg.grupo || undefined, seg_modalidade: seg.modalidade || undefined, seg_uf: seg.uf || undefined } : {};
    api.get("/contabilidade/ans/demonstracoes", { params: { ano, trimestre, comparar: regsSel || undefined, nivel, ...pSeg } })
      .then((r) => { if (vivo) setDados(r.data); })
      .catch((e) => { if (vivo) { setDados(null); toast.error(e?.response?.data?.detail || "Falha ao carregar as demonstrações"); } })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [tri, regsSel, nivel, seg.ativo, seg.grupo, seg.modalidade, seg.uf]);

  // A carga roda em segundo plano no servidor (a 1ª leva minutos): dispara e
  // acompanha pelo status até terminar.
  const sincronizar = () => {
    setSincronizando(true);
    api.post("/contabilidade/ans/sincronizar")
      .then(() => toast("Baixando os balancetes da ANS… a primeira carga leva alguns minutos. Pode seguir usando o sistema."))
      .catch((e) => {
        if (e?.response?.status === 409) toast("Já há uma carga da ANS em andamento.");
        else { toast.error("Falha ao iniciar a carga da ANS"); setSincronizando(false); }
      });
  };
  useEffect(() => {
    if (!sincronizando && !status?.em_andamento) return undefined;
    const t = setInterval(() => {
      api.get("/contabilidade/ans/status").then((r) => {
        setStatus(r.data);
        setTri((x) => x || r.data.trimestres?.[0]?.chave || "");
        if (!r.data.em_andamento) {
          setSincronizando(false);
          const u = r.data.ultima_carga;
          if (u) (u.ok ? toast.success : toast.error)(u.ok ? "Dados da ANS atualizados" : `Carga da ANS com erro: ${u.erro || "ver log"}`);
        }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [sincronizando, status?.em_andamento]);

  const regs = dados ? dados.operadoras.map((o) => o.registro_ans) : [];
  const proprio = status?.registro_proprio;
  const nomeProprio = dados?.operadoras?.find((o) => o.registro_ans === proprio)?.nome;
  const secaoAtual = SECOES.find((s) => s.chave === secao);
  const semDado = dados ? dados.operadoras.filter((o) => !dados.tem_dado[o.registro_ans]) : [];
  const seg2 = (ativo) => ({ fontSize: 12, padding: "5px 10px", border: "none", cursor: "pointer", background: ativo ? T.teal : "#fff", color: ativo ? "#fff" : T.ink, fontWeight: ativo ? 700 : 400 });

  return (
    <div style={{ padding: 20, background: T.bg, minHeight: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <FileSpreadsheet size={22} color={T.teal} />
        <h2 style={{ margin: 0, color: T.ink }}>Demonstrações Contábeis</h2>
        {gestor && (
          <div style={{ marginLeft: "auto" }}>
            <Botao pequeno onClick={sincronizar} disabled={sincronizando || status?.em_andamento}>
              <RefreshCw size={13} className={sincronizando ? "spin" : ""} /> {sincronizando || status?.em_andamento ? "Baixando…" : "Atualizar dados da ANS"}
            </Botao>
          </div>
        )}
      </div>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: T.inkSoft }}>
        Balancete entregue à ANS por cada operadora (DIOPS), publicado nos dados abertos da ANS. Resultado acumulado no ano.
        {status?.ultima_carga && <> Última atualização: {new Date(status.ultima_carga.dt_execucao).toLocaleString("pt-BR")}.</>}
      </p>

      {status && !proprio && (
        <Card style={{ marginBottom: 12, borderColor: T.ambar }}>
          <AlertTriangle size={15} color={T.ambar} /> <b>Registro ANS da operadora não configurado.</b>{" "}
          <span style={{ fontSize: 13, color: T.inkSoft }}>Preencha em <Link to="/admin/operadora">Administração → Operadora</Link> para ela aparecer fixa na primeira coluna.</span>
        </Card>
      )}

      {status && !status.trimestres?.length ? (
        <Card>
          <p style={{ margin: 0, color: T.ink, fontWeight: 600 }}>Os dados da ANS ainda não foram carregados.</p>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: T.inkSoft }}>
            A carga roda sozinha toda segunda-feira de manhã.{gestor ? " Para não esperar, use “Atualizar dados da ANS” acima." : " Peça ao gestor para atualizar agora."}
          </p>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 12, padding: 14 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 13, color: T.inkSoft }}>Trimestre:</span>
                <select value={tri} onChange={(e) => setTri(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.line}` }}>
                  {(status?.trimestres || []).map((t) => <option key={t.chave} value={t.chave}>{t.rotulo}</option>)}
                </select>
              </div>
              <FiltroOperadoras proprio={proprio} nomeProprio={nomeProprio} sel={sel} setSel={setSel} max={status?.max_comparadas || 3} />
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.line}` }}>
              <label style={{ fontSize: 13, color: T.ink, display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", fontWeight: 600 }}>
                <input type="checkbox" checked={seg.ativo} onChange={(e) => setSeg({ ativo: e.target.checked })} />
                Somar segmento
                <InfoTip w={330} texto="Acrescenta uma coluna com a SOMA das operadoras que passam nos filtros ao lado. É o que o painel da ANS mostra. Sem filtro, é o mercado inteiro. Os indicadores do segmento são do conjunto (razão das somas). Use % para comparar com a sua operadora." />
              </label>
              {[
                { campo: "grupo", rot: "Modalidade Grupo", ops: opcoesSeg?.grupos },
                { campo: "modalidade", rot: "Modalidade", ops: opcoesSeg?.modalidades },
                { campo: "uf", rot: "UF da sede", ops: opcoesSeg?.ufs },
              ].map((f) => (
                <label key={f.campo} style={{ fontSize: 12, color: seg.ativo ? T.ink : T.inkSoft, display: "inline-flex", gap: 4, alignItems: "center" }}>
                  {f.rot}:
                  <select value={seg[f.campo]} disabled={!seg.ativo} onChange={(e) => setSeg({ [f.campo]: e.target.value })}
                    style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.line}`, fontSize: 12, maxWidth: 220 }}>
                    <option value="">Todos</option>
                    {(f.ops || []).map((o) => <option key={o.valor} value={o.valor}>{o.valor} ({o.qt})</option>)}
                  </select>
                </label>
              ))}
            </div>
          </Card>

          {carregando && !dados ? <Card><p style={{ color: T.inkSoft }}>Carregando…</p></Card> : dados && (
            <div style={{ opacity: carregando ? 0.6 : 1 }}>
              {semDado.length > 0 && (
                <p style={{ fontSize: 12, color: T.ambar, margin: "0 0 8px" }}>
                  <AlertTriangle size={13} /> Sem balancete publicado em {dados.rotulo} para: {semDado.map((o) => o.nome).join(", ")}.
                </p>
              )}
              <ResultadoLiquido dados={dados} regs={regs} />
              <Indicadores dados={dados} regs={regs} />
              <p style={{ fontSize: 12, color: T.inkSoft, margin: "10px 0 0" }}>
                O IDSS (nota da ANS, comparação com o mercado e a Frente IDSS) agora fica no menu próprio{" "}
                <Link to="/indicadores-ans">Indicadores ANS</Link>.
              </p>

              <Card style={{ marginTop: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                  {regs.length === 1 && (
                    <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 8, overflow: "hidden" }}>
                      <button onClick={() => setLadoALado(true)} style={seg2(ladoALado)}>Lado a lado</button>
                      <button onClick={() => setLadoALado(false)} style={seg2(!ladoALado)}>Por seção</button>
                    </div>
                  )}
                  {!(regs.length === 1 && ladoALado) && <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.line}` }}>
                    {SECOES.map((s) => (
                      <button key={s.chave} onClick={() => setSecao(s.chave)} style={{
                        padding: "8px 14px", border: "none", cursor: "pointer", background: "transparent",
                        borderBottom: `2px solid ${secao === s.chave ? T.teal : "transparent"}`,
                        color: secao === s.chave ? T.teal : T.inkSoft, fontWeight: 600, fontSize: 13 }}>{s.rotulo}</button>
                    ))}
                  </div>}
                  <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 8, overflow: "hidden", marginLeft: "auto" }}
                    title="Em %, cada linha vira proporção: compara operadoras de portes diferentes de forma justa">
                    <button onClick={() => setModo("rs")} style={seg2(modo === "rs")}>R$</button>
                    <button onClick={() => setModo("pct")} style={seg2(modo === "pct")}>%</button>
                  </div>
                  {!(regs.length === 1 && ladoALado) && <label style={{ fontSize: 12, color: T.ink, display: "inline-flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                    <input type="checkbox" checked={comVariacao} onChange={(e) => setComVariacao(e.target.checked)} /> vs {dados.rotulo_anterior}
                  </label>}
                  <label style={{ fontSize: 12, color: T.ink, display: "inline-flex", gap: 4, alignItems: "center" }}>
                    Detalhe:
                    <select value={nivel} onChange={(e) => setNivel(Number(e.target.value))} style={{ padding: "3px 6px", borderRadius: 6, border: `1px solid ${T.line}`, fontSize: 12 }}>
                      <option value={2}>2 níveis</option><option value={3}>3 níveis</option><option value={4}>4 níveis</option>
                      <option value={9}>Completo (até a conta analítica)</option>
                    </select>
                    <InfoTip w={340} texto="Completo abre o plano de contas da sua operadora até o último nível (9 dígitos), por exemplo Despesas com eventos, Glosa e Coparticipação dentro de cada cobertura. Serve para achar valor lançado na conta errada. As operadoras comparadas e o segmento só são publicados pela ANS até 4 dígitos, por isso aparecem vazios abaixo disso." />
                  </label>
                </div>
                {regs.length === 1 && ladoALado ? (
                  // Uma coluna só: Ativo | Passivo | DRE lado a lado, como a página "Demonstrações Contábeis" da ANS
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12 }}>
                    {["ativo", "passivo", "dre"].map((k) => {
                      const s = SECOES.find((x) => x.chave === k);
                      return (
                        <div key={k} style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: 10 }}>
                          <div style={{ fontWeight: 700, color: T.ink, fontSize: 14, marginBottom: 6, textAlign: "center" }}>{s.rotulo.toUpperCase()}</div>
                          <Demonstracao dados={dados} regs={regs} secao={s} modo={modo} comVariacao={false} compacto />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Demonstracao dados={dados} regs={regs} secao={secaoAtual} modo={modo} comVariacao={comVariacao} />
                )}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
